import React, { useState, useEffect, useContext } from "react";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { serverHost } from "variables/serverdata.js";
import HusariaHeader from "views/pages/components/HusariaHeader";
import { useNavigate } from "react-router-dom";
const fmt = (n) => `$${Number(n ?? 0).toFixed(2)}`;

const MOCK_HISTORY = [
  {
    id: 1,
    invoice_name: "S000111",
    date: "2026-07-01",
    recurring_status: "active",
    payment_status: "paid",
    cost: 1249.5,
    modules: [
      { name: "HUCS", qty: 25 },
      { name: "HRMS", qty: 10 },
      { name: "HADM", qty: 5 },
    ],
  },
  {
    id: 2,
    invoice_name: "S00013",
    date: "2026-06-01",
    recurring_status: "active",
    payment_status: "paid",
    cost: 1180.0,
    modules: [
      { name: "HUCS", qty: 24 },
      { name: "HRMS", qty: 10 },
    ],
  },
  {
    id: 3,
    invoice_name: "S0001",
    date: "2026-05-01",
    recurring_status: "renewal",
    payment_status: "unpaid",
    cost: 1180.0,
    modules: [
      { name: "HUCS", qty: 24 },
      { name: "HRMS", qty: 10 },
    ],
  },
  {
    id: 4,
    invoice_name: "S00014",
    date: "2026-04-01",
    recurring_status: "active",
    payment_status: "paid",
    cost: 940.75,
    modules: [
      { name: "HUCS", qty: 20 },
      { name: "HANS", qty: 3 },
    ],
  },
  {
    id: 5,
    invoice_name: "S000111",
    date: "2026-03-01",
    recurring_status: "cancelled",
    payment_status: "overdue",
    cost: 610.0,
    modules: [{ name: "HUCS", qty: 15 }],
  },
];

const moduleColour = {
  HUCS: "#ED1904",
  HADM: "#1D9808",
  HRMS: "#ED7C02",
  HANS: "#334B63",
};
function SubscriptionsHistory() {
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const client_uuid = localStorage.getItem("clientId");

  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(
        `${serverHost}/get_previous_invoices/${client_uuid}`,
        { method: "GET" },
      );
      if (!resp.ok) throw new Error("Failed to fetch subscription history");
      const data = (await resp.json()).data;
      setHistory(data ?? []);
    } catch (err) {
      console.error("Failed to fetch subscription history -", err);
      setError(err.message);
      setHistory(MOCK_HISTORY);
    } finally {
      setLoading(false);
    }
  };

  const downloadInvoice = async (row) => {
    try {
      const resp = await fetch(
        `${serverHost}/download_previous_invoice/${row.id}`,
        { method: "GET" },
      );
      if (!resp.ok) {
        const err = await resp.json();
        throw new Error(err.error || "Failed to fetch invoice");
      }
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${row.invoice_name || "invoice"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download invoice:", err);
    }
  };

  /* ─── styles ───────────────────────────────────────────────── */
  const card = isDark ? "#1e2737" : "#ffffff";
  const border = isDark ? "#2d3748" : "#e2e6ea";
  const text = isDark ? "#e2e8f0" : "#1a202c";
  const muted = isDark ? "#94a3b8" : "#64748b";
  const accent = "#3b82f6";

  const recurringStatusMap = {
    active: { label: "Active", color: "#10b981", bg: "#10b98120" },
    renewal: { label: "Renewal", color: "#f59e0b", bg: "#f59e0b20" },
    cancelled: { label: "Cancelled", color: "#ef4444", bg: "#ef444420" },
  };

  const paymentStatusMap = {
    paid: { label: "Paid", color: "#10b981", bg: "#10b98120" },
    unpaid: { label: "Unpaid", color: "#f59e0b", bg: "#f59e0b20" },
    overdue: { label: "Overdue", color: "#ef4444", bg: "#ef444420" },
    in_payment: { label: "In Payment", color: "#2EB2FF", bg: "#2EB2FF20" },
  };

  const btnStyle = (color, size) => ({
    background: color,
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    padding: size === "sm" ? "0.3rem 0.65rem" : "0.5rem 1rem",
    fontSize: size === "sm" ? "0.75rem" : "0.85rem",
    fontWeight: 700,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "0.3rem",
  });
  const StatusPill = ({ statusKey, map }) => {
    const info = map[statusKey] ?? {
      label: statusKey ?? "—",
      color: muted,
      bg: "transparent",
    };
    return (
      <span
        style={{
          fontSize: "0.72rem",
          fontWeight: 700,
          color: info.color,
          background: info.bg,
          padding: "0.1rem 0.5rem",
          borderRadius: "5px",
        }}
      >
        {info.label}
      </span>
    );
  };

  if (loading)
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "60vh",
          color: muted,
        }}
      >
        Loading…
      </div>
    );

  return (
    <>
      <HusariaHeader
        name="Subscription History"
        parentName="Admin"
        padding="3rem"
      />
      <div style={{ minHeight: "100vh", padding: "1.5rem 2rem" }}>
        <div style={{ marginBottom: "0.4rem" }}>
          <button
            onClick={() => {
              navigate("/admin/subscription");
            }}
            className="export-btn"
            style={btnStyle("#FFA12E")}
          >
            <i className="fas fa-receipt" style={{ marginBottom: "0.2rem" }} />{" "}
            Subscriptions
          </button>
        </div>
        {error && (
          <div
            style={{
              color: "#ef4444",
              fontSize: "0.85rem",
              marginBottom: "1rem",
            }}
          >
            {error}
          </div>
        )}

        {history.length === 0 ? (
          <div
            style={{
              fontSize: "0.85rem",
              color: muted,
              padding: "1rem",
              background: card,
              border: `1px solid ${border}`,
              borderRadius: "8px",
            }}
          >
            No subscription history found.
          </div>
        ) : (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}
          >
            {history.map((row, i) => (
              <div
                key={row.id ?? i}
                style={{
                  background: card,
                  border: `1.5px solid ${border}`,
                  borderRadius: "10px",
                  padding: "1rem 1.25rem",
                }}
              >
                {/* ── top meta line ── */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "0.85rem",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: "1.5rem",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "0.95rem",
                        fontWeight: 800,
                        color: text,
                      }}
                    >
                      {row.invoice_name}
                    </div>
                    <div>
                      <div style={metaLabelStyle(muted)}>Date</div>
                      <div
                        style={{
                          fontSize: "0.82rem",
                          color: text,
                          fontWeight: 600,
                        }}
                      >
                        {row.date}
                      </div>
                    </div>
                    <div>
                      <div style={metaLabelStyle(muted)}>Recurring status</div>
                      <StatusPill
                        statusKey={row.recurring_status}
                        map={recurringStatusMap}
                      />
                    </div>
                    <div>
                      <div style={metaLabelStyle(muted)}>Payment status</div>
                      <StatusPill
                        statusKey={row.payment_status}
                        map={paymentStatusMap}
                      />
                    </div>
                  </div>

                  <button
                    onClick={() => downloadInvoice(row)}
                    className="export-btn"
                    style={btnStyle("#8b5cf6", "sm")}
                  >
                    <i
                      className="fas fa-download"
                      style={{ marginRight: "0.3rem" }}
                    />
                    Download
                  </button>
                </div>

                {/* ── cost / modules ── */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    alignItems: "start",
                    gap: "1rem",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        color: accent,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        marginBottom: "0.2rem",
                      }}
                    >
                      Cost
                    </div>
                    <div
                      style={{
                        fontSize: "2rem",
                        fontWeight: 800,
                        color: text,
                        lineHeight: 1,
                      }}
                    >
                      {fmt(row.cost)}
                    </div>
                  </div>

                  <div>
                    <div
                      style={{
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        color: "#8b5cf6",
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        marginBottom: "0.4rem",
                      }}
                    >
                      Modules
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "0.4rem",
                      }}
                    >
                      {(row.modules ?? []).map((m, idx) => (
                        <span
                          key={idx}
                          style={{
                            fontSize: "0.78rem",
                            fontWeight: 700,
                            color: moduleColour[m.name],
                            background: "#CFCFCF",
                            padding: "0.2rem 0.6rem",
                            borderRadius: "5px",
                          }}
                        >
                          {typeof m === "string"
                            ? m
                            : `${m.name}${m.qty ? ` (${m.qty})` : ""}`}
                        </span>
                      ))}
                      {(!row.modules || row.modules.length === 0) && (
                        <span style={{ fontSize: "0.8rem", color: muted }}>
                          —
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

const metaLabelStyle = (muted) => ({
  fontSize: "0.68rem",
  fontWeight: 700,
  color: muted,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  marginBottom: "0.1rem",
});

export default SubscriptionsHistory;
